/**
 * Sprint 208 Master item #6 — Active Launch Funnel tile.
 *
 * Sits on the Command Centre right next to the Templates Fired card.
 * Pulls GET /api/onboarding/metrics/funnel and surfaces three numbers
 * (Signups Today / Charters Pending Countersign / Free-to-Premium %)
 * plus a 24h sparkline of signups so the founder can see if either
 * onboarding pipeline is dripping users in real time.
 */
import React, { useEffect, useState } from 'react';
import { TrendingUp, FileSignature, Users, RefreshCcw, Loader2 } from 'lucide-react';
import { apiClient } from '../../api/client';

const Sparkline = ({ buckets = [] }) => {
  const max = Math.max(1, ...buckets);
  const w = 220, h = 36;
  const step = buckets.length > 1 ? w / (buckets.length - 1) : 0;
  const pts = buckets
    .map((v, i) => `${(i * step).toFixed(1)},${(h - (v / max) * (h - 4) - 2).toFixed(1)}`)
    .join(' ');
  return (
    <svg width={w} height={h} className="overflow-visible" data-testid="launch-funnel-sparkline">
      <polyline fill="none" stroke="#10b981" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" points={pts} />
      {buckets.map((v, i) => (
        <circle key={i} cx={i * step} cy={h - (v / max) * (h - 4) - 2} r={v > 0 ? 2.2 : 1.2} fill={v > 0 ? '#10b981' : '#d4d4d8'} />
      ))}
    </svg>
  );
};

const ActiveLaunchFunnelCard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const reload = async () => {
    setLoading(true);
    try {
      const resp = await apiClient.request('/api/onboarding/metrics/funnel');
      setData(resp);
    } catch (e) {
      if ((e?.message || '').includes('admin_only') || (e?.message || '').includes('403')) {
        setData('hidden');
      }
    } finally { setLoading(false); }
  };
  useEffect(() => { reload(); }, []);

  if (data === 'hidden') return null;

  return (
    <div
      className="bg-white rounded-lg border border-gray-200 shadow-sm px-5 py-4 flex flex-wrap items-center gap-5"
      data-testid="launch-funnel-card"
    >
      <div className="flex items-center gap-2.5">
        <div className="w-9 h-9 rounded-md flex items-center justify-center bg-emerald-50">
          <TrendingUp size={18} className="text-emerald-600" />
        </div>
        <div>
          <h3 className="text-sm font-bold text-gray-900">📈 Active Launch Funnel</h3>
          <p className="text-xs text-gray-500">Real-time pulse on both Path A &amp; Path B onboarding</p>
        </div>
      </div>

      <div className="flex items-baseline gap-1.5" data-testid="funnel-signups">
        <span className="text-3xl font-bold text-gray-900">{loading ? '—' : (data?.signups_today ?? 0)}</span>
        <span className="text-xs text-gray-500 font-medium">signups today</span>
      </div>

      <div className="flex items-center gap-1.5 text-xs text-gray-600" data-testid="funnel-pending">
        <FileSignature size={13} className="text-purple-400" />
        <span className="font-semibold text-gray-900">{loading ? '—' : (data?.charters_pending_countersign ?? 0)}</span>
        <span>charters pending</span>
      </div>

      <div className="flex items-center gap-1.5 text-xs text-gray-600" data-testid="funnel-conversion">
        <Users size={13} className="text-amber-400" />
        <span className="font-semibold text-gray-900">{loading ? '—' : `${data?.free_to_premium_pct ?? 0}%`}</span>
        <span>free→premium</span>
      </div>

      <div className="flex-1 min-w-[220px]">
        {loading ? (
          <div className="h-9 flex items-center text-xs text-gray-400">
            <Loader2 className="w-3.5 h-3.5 animate-spin mr-2" /> loading…
          </div>
        ) : (
          <Sparkline buckets={data?.sparkline_signups || []} />
        )}
      </div>

      <button
        onClick={reload}
        className="p-1.5 rounded-md bg-gray-100 hover:bg-gray-200 text-gray-600"
        title="Reload"
        data-testid="launch-funnel-reload"
        disabled={loading}
      >
        <RefreshCcw size={13} className={loading ? 'animate-spin' : ''} />
      </button>
    </div>
  );
};

export default ActiveLaunchFunnelCard;
